<template>
  <div class="admin-owe-fee-detail-container">
    <el-row :gutter="16">
      <!-- 左侧楼栋树：保留原有组件与事件，不改任何业务逻辑 -->
      <el-col :xs="24" :sm="24" :md="5" :lg="4" class="room-floor-unit-tree">
        <community-floor-tree ref="communityFloorTree" @chooseFloor="handleChooseFloor" />
      </el-col>

      <el-col :xs="24" :sm="24" :md="19" :lg="20">
        <!-- (1) PageHeader 标题 + 副标题 -->
        <ds-page-header
          :title="$t('adminOweFeeDetail.page.title')"
          :subtitle="$t('adminOweFeeDetail.page.subtitle')">
          <template #extra>
            <el-tag type="danger" effect="light" class="ds-page-header__owe-tag">
              {{ $t('adminOweFeeDetail.page.oweTip') }}
            </el-tag>
          </template>
        </ds-page-header>

        <!-- (2) SearchPanel 筛选区：字段顺序保持不变，仅重排栅格与按钮位置 -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.feeTypeCd" :placeholder="$t('adminOweFeeDetail.search.feeTypeCd')" clearable
                class="w-full">
                <el-option v-for="item in feeTypeCds" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="searchForm.objNameLike" :placeholder="$t('adminOweFeeDetail.search.objNameLike')"
                clearable />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.startTime" type="date"
                :placeholder="$t('adminOweFeeDetail.search.startTime')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.endTime" type="date"
                :placeholder="$t('adminOweFeeDetail.search.endTime')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="searchForm.ownerNameLike" :placeholder="$t('adminOweFeeDetail.search.ownerNameLike')"
                clearable />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(5)(6) Toolbar + DataTable + 合计区 + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density"
            :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminOweFeeDetail.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('adminOweFeeDetail.page.empty')" @retry="getList">
            <el-table-column prop="index" :label="$t('adminOweFeeDetail.table.index')" width="90" align="center" />
            <el-table-column prop="communityName" :label="$t('adminOweFeeDetail.table.communityName')" align="left"
              min-width="120" />
            <el-table-column prop="objName" :label="$t('adminOweFeeDetail.table.objName')" align="left" width="130" />
            <el-table-column prop="ownerName" :label="$t('adminOweFeeDetail.table.ownerName')" align="left" width="110" />
            <el-table-column prop="ownerTel" :label="$t('adminOweFeeDetail.table.ownerTel')" align="center" width="130" />
            <el-table-column :label="$t('adminOweFeeDetail.table.builtUpArea')" align="right" width="100">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.builtUpArea, 0) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="feeName" :label="$t('adminOweFeeDetail.table.feeName')" align="left" min-width="130" />
            <el-table-column :label="$t('adminOweFeeDetail.table.startTime')" align="center" width="120">
              <template slot-scope="scope">
                {{ formatDate(scope.row.startTime) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminOweFeeDetail.table.endTime')" align="center" width="120">
              <template slot-scope="scope">
                {{ formatDate(scope.row.endTime) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminOweFeeDetail.table.oweDay')" align="right" width="120">
              <template slot-scope="scope">
                <ds-status-tag v-if="isOverdue(scope.row)" type="danger" size="sm">
                  {{ scope.row.oweDay }} {{ $t('adminOweFeeDetail.page.dayUnit') }}
                </ds-status-tag>
                <span v-else class="ds-num">{{ scope.row.oweDay }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminOweFeeDetail.table.oweAmount')" align="right" width="130">
              <template slot-scope="scope">
                <span class="ds-num ds-num--danger">{{ formatMoney(scope.row.oweAmount) }}</span>
              </template>
            </el-table-column>
          </ds-data-table>

          <!-- 合计区：小计 / 大计口径与改造前完全一致 -->
          <div class="ds-summary">
            <div class="ds-summary__row">
              <div class="ds-summary__cell ds-summary__cell--head">{{ $t('adminOweFeeDetail.summary.subtotal') }}</div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminOweFeeDetail.summary.owe') }}</span>
                <span class="ds-num ds-num--danger">{{ totalPreferentialAmount }}</span>
                <span class="ds-summary__unit">{{ $t('adminOweFeeDetail.summary.yuan') }}</span>
              </div>
            </div>
            <div class="ds-summary__row ds-summary__row--total">
              <div class="ds-summary__cell ds-summary__cell--head">{{ $t('adminOweFeeDetail.summary.total') }}</div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminOweFeeDetail.summary.owe') }}</span>
                <span class="ds-num ds-num--danger">{{ allOweAmount }}</span>
                <span class="ds-summary__unit">{{ $t('adminOweFeeDetail.summary.yuan') }}</span>
              </div>
            </div>
            <div class="ds-summary__notes">
              <div>{{ $t('adminOweFeeDetail.tips.startTime') }}</div>
              <div>{{ $t('adminOweFeeDetail.tips.oweDayDeposit') }}</div>
              <div>{{ $t('adminOweFeeDetail.tips.oweDayOther') }}</div>
              <div>{{ $t('adminOweFeeDetail.tips.oweAmount') }}</div>
            </div>
          </div>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>


<script>
import { queryAdminOweFeeDetail } from '@/api/fee/adminOweFeeDetailApi'
import CommunityFloorTree from '@/components/fee/communityFloorTree'
import { formatNumber, formatMoney, formatDate } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminOweFeeDetailList',
  components: {
    CommunityFloorTree,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        floorId: '',
        floorName: '',
        roomNum: '',
        objNameLike: '',
        feeTypeCd: '',
        startTime: '',
        endTime: '',
        communityId: '',
        ownerNameLike: ''
      },
      feeTypeCds: [],
      tableData: [],
      totalPreferentialAmount: 0,
      allOweAmount: 0,
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.initData()
  },
  methods: {
    async initData() {
      try {
        this.loading = true
        await this.getFeeTypeCds()
        await this.getList()
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async getFeeTypeCds() {
      // 这里应该是获取费用类型的字典数据
      // 假设从字典接口获取数据
      this.feeTypeCds = [
        { statusCd: '1', name: this.$t('adminOweFeeDetail.feeType.property') },
        { statusCd: '2', name: this.$t('adminOweFeeDetail.feeType.parking') }
      ]
    },
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const res = await queryAdminOweFeeDetail(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况，
        // 原实现直接 data.map() 会抛 TypeError，导致筛选后表格仍停留在上一次的数据。
        const list = Array.isArray(res.data) ? res.data : []
        const total = res.total || 0

        this.tableData = list.map((item, index) => ({
          ...item,
          index: index + 1
        }))
        this.pagination.total = total

        // 计算小计（口径与改造前一致：当前页各行 oweAmount 之和）
        let _totalPreferentialAmount = 0.0
        list.forEach(item => {
          _totalPreferentialAmount += parseFloat(item.oweAmount)
        })
        this.totalPreferentialAmount = _totalPreferentialAmount.toFixed(2)

        if (list.length > 0) {
          // 统一保留 2 位小数：原实现直接透传后端 allOweAmount，
          // 会出现「205405.9485」这类 4 位小数，与小计的 2 位口径不一致。
          this.allOweAmount = formatNumber(list[0].allOweAmount, 2)
        } else {
          this.allOweAmount = '0.00'
        }
      } catch (error) {
        console.error(error)
        this.hasError = true
        this.errorMessage = this.$t('adminOweFeeDetail.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        floorId: '',
        floorName: '',
        roomNum: '',
        objNameLike: '',
        feeTypeCd: '',
        startTime: '',
        endTime: '',
        communityId: '',
        ownerNameLike: ''
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleChooseFloor(floor) {
      this.searchForm.floorId = floor.floorId
      this.searchForm.floorName = floor.floorNum
      this.searchForm.communityId = floor.communityId
      this.searchForm.communityName = floor.communityName
      this.handleSearch()
    },
    /**
     * 欠费时长是否已超过 90 天（阈值仅用于前端高亮提示，不改变任何业务计算口径）
     */
    isOverdue(row) {
      const days = parseInt(row && row.oweDay, 10)
      return !isNaN(days) && days >= 90
    },
    formatNumber(value, decimals = 2) {
      return formatNumber(value, decimals)
    },
    formatMoney(value) {
      return formatMoney(value)
    },
    formatDate(value) {
      return formatDate(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-owe-fee-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);

  /* 左侧楼栋树卡片 */
  ::v-deep .tree-card {
    border-radius: var(--ds-radius-md);
    border: 1px solid var(--ds-gray-300);
    box-shadow: var(--ds-shadow-sm);
    max-height: calc(100vh - 140px);
    overflow-y: auto;
  }

  .room-floor-unit-tree {
    padding-right: 0;
  }
}

/* 标题区右上角提示标签 */
.ds-page-header__owe-tag {
  font-size: var(--ds-font-size-xs);
}

/* 表格 + 合计 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 合计区 */
.ds-summary {
  padding: var(--ds-spacing-md) var(--ds-spacing-lg);
  background: var(--ds-gray-50);
  border-top: 1px solid var(--ds-gray-200);

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ds-spacing-md);
    padding: var(--ds-spacing-xs) 0;

    &--total {
      border-top: 1px dashed var(--ds-gray-300);
      margin-top: var(--ds-spacing-xs);
      padding-top: var(--ds-spacing-sm);

      .ds-summary__cell {
        font-weight: 600;
      }
    }
  }

  &__cell {
    display: flex;
    align-items: baseline;
    gap: var(--ds-spacing-xs);
    min-width: 120px;

    &--head {
      font-weight: 600;
      color: var(--ds-gray-800);
    }
  }

  &__label {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }

  &__unit {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }

  &__notes {
    margin-top: var(--ds-spacing-sm);
    padding-top: var(--ds-spacing-sm);
    border-top: 1px solid var(--ds-gray-200);
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    line-height: 1.8;
  }
}

/* 欠费金额：使用危险色突出（颜色仅作辅助，数字本身已足够表意） */
.ds-num--danger {
  color: var(--ds-danger);
  font-weight: 600;
}
</style>
